/**
 * Temporary stand-in for the ID scanner.
 * Remove this when the Ambir scanner is connected.
 *
 * A photo upload is not a scan. The name and birth date are filled from a
 * barcode when one can be read. Otherwise an optional printed-text reader may
 * fill the same fields. Nothing is invented when neither source returns them.
 * The portrait crop follows the largest face on the card. Horizontal and
 * vertical licenses do not put the photo in the same place.
 */

import { toVisitorScan } from './idScanner';
import { readLicenseBarcodeTexts } from './pdf417Decode';

function splitDataUrl(dataUrl) {
  const match = String(dataUrl || '').match(/^data:([^;]+);base64,(.+)$/);
  if (!match) return null;
  return { mimeType: match[1], base64Data: match[2] };
}

async function widestImage(files) {
  if (typeof createImageBitmap !== 'function') return 0;
  let width = 0;
  for (const file of files) {
    if (!file) continue;
    try {
      const bitmap = await createImageBitmap(file);
      width = Math.max(width, bitmap.width || 0);
      if (typeof bitmap.close === 'function') bitmap.close();
    } catch (error) {
      width = width;
    }
  }
  return width;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('The image could not be read.'));
    reader.readAsDataURL(file);
  });
}

export async function decodeBarcodeTexts(file) {
  return readLicenseBarcodeTexts(file);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export function portraitCropFromFaces(faces, imageWidth, imageHeight) {
  const boxes = (faces || [])
    .map((face) => face?.boundingBox || face)
    .filter((box) => box && box.width > 0 && box.height > 0);
  if (!boxes.length || imageWidth < 2 || imageHeight < 2) return null;
  const box = boxes.reduce((best, item) => (
    item.width * item.height > best.width * best.height ? item : best
  ));
  const size = Math.max(box.width, box.height);
  const centerX = box.x + box.width / 2;
  let cropWidth = size * 1.6;
  let cropHeight = size * 1.48;
  const scale = Math.min(1, imageWidth / cropWidth, imageHeight / cropHeight);
  cropWidth *= scale;
  cropHeight *= scale;
  const x = clamp(centerX - cropWidth / 2, 0, imageWidth - cropWidth);
  const y = clamp(box.y - size * 0.2, 0, imageHeight - cropHeight);
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.max(1, Math.round(cropWidth)),
    height: Math.max(1, Math.round(cropHeight)),
  };
}

function rotatedCanvas(image, degrees) {
  const canvas = document.createElement('canvas');
  const quarter = degrees === 90 || degrees === 270;
  canvas.width = quarter ? image.height : image.width;
  canvas.height = quarter ? image.width : image.height;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate((degrees * Math.PI) / 180);
  context.drawImage(image, -image.width / 2, -image.height / 2);
  return canvas;
}

let faceDetectorPromise;

function mediapipeAsset(path) {
  return `${process.env.PUBLIC_URL || ''}/mediapipe/${path}`;
}

export function textLineScore(samples, width, height) {
  const pixels = samples || [];
  const rowCount = Math.max(0, Number(height) || 0);
  const columnCount = Math.max(0, Number(width) || 0);
  if (!rowCount || !columnCount || pixels.length < rowCount * columnCount) return 0;
  const rows = new Array(rowCount).fill(0);
  let dark = 0;
  for (let y = 0; y < rowCount; y += 1) {
    for (let x = 0; x < columnCount; x += 1) {
      if (pixels[y * columnCount + x]) {
        rows[y] += 1;
        dark += 1;
      }
    }
  }
  if (dark < rowCount * columnCount * 0.01) return 0;
  const mean = rows.reduce((sum, value) => sum + value, 0) / rowCount;
  const variance = rows.reduce((sum, value) => sum + ((value - mean) ** 2), 0) / rowCount;
  return variance / (columnCount * columnCount);
}

export function chooseCardRotation(candidates) {
  const list = (candidates || []).filter((item) => Number.isInteger(item?.degrees));
  if (!list.length) return 0;
  const scoreOf = (item, key) => Number(item?.[key]) || 0;
  const bestFace = list.reduce((best, item) => (scoreOf(item, 'faceScore') > scoreOf(best, 'faceScore') ? item : best));
  if (scoreOf(bestFace, 'faceScore') >= 0.5) return bestFace.degrees;
  const bestText = list.reduce((best, item) => (scoreOf(item, 'textScore') > scoreOf(best, 'textScore') ? item : best));
  const current = list.find((item) => item.degrees === 0) || list[0];
  const currentText = Math.max(scoreOf(current, 'textScore'), 0.000001);
  if (bestText.degrees !== 0 && scoreOf(bestText, 'textScore') / currentText >= 1.35) return bestText.degrees;
  if (scoreOf(bestFace, 'faceScore') > scoreOf(current, 'faceScore') + 0.15) return bestFace.degrees;
  return 0;
}

function sampleDarkGrid(source, maxEdge = 160) {
  const scale = Math.min(1, maxEdge / Math.max(source.width || 1, source.height || 1));
  const width = Math.max(1, Math.round((source.width || 1) * scale));
  const height = Math.max(1, Math.round((source.height || 1) * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  context.drawImage(source, 0, 0, width, height);
  const data = context.getImageData(0, 0, width, height).data;
  const dark = new Uint8Array(width * height);
  for (let index = 0; index < dark.length; index += 1) {
    const offset = index * 4;
    const luminance = data[offset] * 0.299 + data[offset + 1] * 0.587 + data[offset + 2] * 0.114;
    dark[index] = luminance < 145 ? 1 : 0;
  }
  return { dark, width, height };
}

function largestFace(detections) {
  return (detections || []).reduce((best, item) => {
    const box = item?.boundingBox;
    const area = box ? (box.width || 0) * (box.height || 0) : 0;
    return area > (best?.area || 0) ? { item, area } : best;
  }, null);
}

async function uprightSource(image) {
  let detector = null;
  try {
    detector = await loadFaceDetector();
  } catch (error) {
    detector = null;
  }
  const candidates = [];
  for (const degrees of [0, 90, 270, 180]) {
    const source = degrees ? rotatedCanvas(image, degrees) : image;
    if (!source) continue;
    let faceScore = 0;
    if (detector) {
      try {
        const found = largestFace(detector.detect(source).detections);
        if (found) faceScore = faceUprightScore(found.item.keypoints);
      } catch (error) {
        faceScore = 0;
      }
    }
    const grid = sampleDarkGrid(source);
    const textScore = grid ? textLineScore(grid.dark, grid.width, grid.height) : 0;
    candidates.push({ degrees, faceScore, textScore, source });
  }
  const degrees = chooseCardRotation(candidates);
  return candidates.find((item) => item.degrees === degrees)?.source || image;
}

export function orientIdCard(sourceUrl) {
  return new Promise((resolve) => {
    if (!sourceUrl) {
      resolve('');
      return;
    }
    const image = new Image();
    image.onload = async () => {
      try {
        const oriented = await uprightSource(image);
        if (!oriented || oriented === image) {
          resolve(sourceUrl);
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = oriented.width;
        canvas.height = oriented.height;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(sourceUrl);
          return;
        }
        context.drawImage(oriented, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', 0.92));
      } catch (error) {
        resolve(sourceUrl);
      }
    };
    image.onerror = () => resolve(sourceUrl);
    image.src = sourceUrl;
  });
}

export function faceUprightScore(keypoints) {
  if (!Array.isArray(keypoints) || keypoints.length < 4) return 0;
  const eyeA = keypoints[0];
  const eyeB = keypoints[1];
  const mouth = keypoints[3];
  if (!eyeA || !eyeB || !mouth) return 0;
  const eyeSpan = Math.hypot(eyeB.x - eyeA.x, eyeB.y - eyeA.y);
  if (eyeSpan < 0.001) return 0;
  const level = 1 - Math.min(Math.abs(eyeB.y - eyeA.y) / eyeSpan, 1);
  const drop = (mouth.y - (eyeA.y + eyeB.y) / 2) / eyeSpan;
  const below = drop > 0.2 ? 1 : 0;
  const facing = Math.max(0, Math.min(drop, 1.2)) / 1.2;
  return level * 0.55 + below * 0.25 + facing * 0.2;
}

function detectionBoxes(detections) {
  return (detections || []).map((item) => {
    const box = item.boundingBox || item;
    return {
      x: box.originX ?? box.x ?? 0,
      y: box.originY ?? box.y ?? 0,
      width: box.width || 0,
      height: box.height || 0,
    };
  });
}

function loadFaceDetector() {
  if (!faceDetectorPromise) {
    faceDetectorPromise = import('@mediapipe/tasks-vision').then(async (vision) => {
      const fileset = await vision.FilesetResolver.forVisionTasks(mediapipeAsset('wasm'));
      return vision.FaceDetector.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: mediapipeAsset('blaze_face_short_range.tflite') },
        runningMode: 'IMAGE',
        minDetectionConfidence: 0.4,
      });
    }).catch((error) => {
      faceDetectorPromise = null;
      throw error;
    });
  }
  return faceDetectorPromise;
}

async function faceCrop(image) {
  let detector;
  try {
    detector = await loadFaceDetector();
  } catch (error) {
    return null;
  }
  let best = null;
  for (const degrees of [0, 90, 270, 180]) {
    const source = degrees ? rotatedCanvas(image, degrees) : image;
    if (!source) continue;
    let detections = [];
    try {
      detections = detector.detect(source).detections || [];
    } catch (error) {
      continue;
    }
    const detection = detections.reduce((largest, item) => {
      const box = item.boundingBox;
      const area = box ? (box.width || 0) * (box.height || 0) : 0;
      return area > (largest?.area || 0) ? { item, area } : largest;
    }, null);
    if (!detection) continue;
    const crop = portraitCropFromFaces(detectionBoxes([detection.item]), source.width, source.height);
    if (!crop) continue;
    const score = faceUprightScore(detection.item.keypoints);
    if (!best || score > best.score + 0.05) best = { source, crop, score };
  }
  return best;
}

export function cropLicensePortrait(dataUrl) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = async () => {
      const found = await faceCrop(image);
      if (!found) {
        resolve(dataUrl);
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = found.crop.width;
      canvas.height = found.crop.height;
      const context = canvas.getContext('2d');
      if (!context) {
        resolve(dataUrl);
        return;
      }
      context.drawImage(
        found.source,
        found.crop.x,
        found.crop.y,
        found.crop.width,
        found.crop.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    image.onerror = () => resolve(dataUrl);
    image.src = dataUrl;
  });
}

async function imageRecord(file, decodeBarcodes, readFile) {
  const dataUrl = await readFile(file);
  const parts = splitDataUrl(dataUrl);
  if (!parts) return null;
  const texts = await decodeBarcodes(file);
  return {
    ...parts,
    barcodes: texts.map((text) => ({ text, barcodeType: 'PDF_417', isAamva: /ANSI|DCS|DAQ/.test(text) })),
  };
}

export async function buildTestIdScan(frontFile, backFile, {
  decodeBarcodes = decodeBarcodeTexts,
  cropPortrait = cropLicensePortrait,
  readFile = readFileAsDataUrl,
  readPrintedText = null,
} = {}) {
  if (!frontFile && !backFile) {
    return {
      available: true,
      scanned: false,
      fullName: '',
      imageDataUrl: null,
      message: 'Choose a front or back image.',
    };
  }
  const images = [];
  if (frontFile) images.push(await imageRecord(frontFile, decodeBarcodes, readFile));
  if (backFile) images.push(await imageRecord(backFile, decodeBarcodes, readFile));
  const scan = await toVisitorScan(images.filter(Boolean));
  if (scan.imageDataUrl) {
    scan.portraitDataUrl = await cropPortrait(scan.imageDataUrl);
  }
  const fromBarcode = Boolean(scan.identityExtracted);
  let fromPrint = false;
  let readerMessage = '';
  if (!fromBarcode && frontFile && typeof readPrintedText === 'function') {
    try {
      const printed = await readPrintedText(frontFile);
      scan.firstName = printed?.firstName || '';
      scan.middleName = printed?.middleName || '';
      scan.lastName = printed?.lastName || '';
      scan.suffix = printed?.suffix || '';
      scan.birthDate = printed?.birthDate || '';
      scan.fullName = [scan.firstName, scan.middleName, scan.lastName, scan.suffix].filter(Boolean).join(' ');
      scan.identityExtracted = Boolean(scan.firstName && scan.lastName && scan.birthDate);
      scan.scanned = Boolean(scan.fullName || scan.imageDataUrl);
      fromPrint = Boolean(scan.firstName || scan.lastName || scan.birthDate);
    } catch (error) {
      readerMessage = error?.message || 'The picture could not be read.';
    }
  }
  if (fromBarcode) {
    scan.message = 'Test images received. The name and birth date were read from a barcode.';
  } else if (scan.identityExtracted) {
    scan.message = 'Test images received. The name and birth date were read from the printed card. Check them before check-in.';
  } else if (fromPrint) {
    scan.message = 'Some printed details were read. Check them and fill in anything still blank.';
  } else if (readerMessage && !/not configured/i.test(readerMessage)) {
    scan.message = readerMessage;
  } else {
    const width = await widestImage([frontFile, backFile]);
    scan.message = width > 0 && width < 1400
      ? 'The barcode was not read. This scan is too small for that barcode. Scan the card again at a higher resolution.'
      : 'Test images received. No name or birth date was read from a barcode, so nothing was filled in from the picture.';
  }
  return scan;
}
